import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Container, Button } from 'react-bootstrap';
import MainNav from '../Components/MainNav';
import Footer from '../Components/Footer';

// Set up the worker using CDN with https
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const tableOfContents = [
  { name: 'Starters', page: 1, icon: '🥟' },
  { name: 'BBQ Special', page: 2, icon: '🍖' },
  { name: 'Soups & Salads', page: 3, icon: '🥗' },
  { name: 'Chicken Dishes', page: 4, icon: '🍗' },
  { name: 'Mutton Karahi', page: 5, icon: '🥘' },
  { name: 'Beef Specialties', page: 6, icon: '🥩' },
  { name: 'Fresh Seafood', page: 7, icon: '🦐' },
  { name: 'Biryani & Rice', page: 8, icon: '🍚' },
  { name: 'Tandoori Breads', page: 9, icon: '🍞' },
  { name: 'Beverages', page: 10, icon: '🥤' },
  { name: 'Royal Desserts', page: 11, icon: '🍰' },
];

const Menu = () => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(800);

  // Handle responsive width
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 576) {
        setPageWidth(screenWidth - 48);
      } else if (screenWidth < 768) {
        setPageWidth(520);
      } else if (screenWidth < 1200) {
        setPageWidth(720);
      } else {
        setPageWidth(860);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPage = (page) => {
    setPageNumber(page);
  };

  const previousPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const nextPage = () => {
    setPageNumber((prev) => Math.min(numPages || prev, prev + 1));
  };

  return (
    <div className="menu-page-wrapper luxury-dark-page">
      <MainNav />

      {/* Hero Header */}
      <section className="luxury-page-hero text-center">
        <Container>
          <div className="luxury-pill-badge luxury-pill-badge--gold">
            <span>⚜ MASTERPIECE MENUS ⚜</span>
          </div>
          <h1 className="luxury-page-hero__title">Imperial Dining Menu</h1>
          <p className="luxury-page-hero__subtitle">
            Explore authentic Mughlai delicacies, live charcoal barbecues, and generational recipes prepared by master Lahori chefs.
          </p>

          {/* Quick-Jump Category Navigation */}
          <div className="luxury-menu-toc-wrapper">
            <div className="luxury-menu-toc">
              {tableOfContents.map((item) => {
                const isActive = pageNumber === item.page;
                return (
                  <button
                    key={item.page}
                    onClick={() => goToPage(item.page)}
                    className={`luxury-toc-pill ${isActive ? 'active' : ''}`}
                  >
                    <span className="toc-icon">{item.icon}</span>
                    <span className="toc-text">{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* Main PDF Canvas Section */}
      <Container className="pb-5">
        <div className="luxury-pdf-reader-card">
          {/* Reader Top Controls */}
          <div className="pdf-reader-header">
            <div className="reader-badge">
              <span className="dot" />
              <span>Official 2026 Haveli Menu</span>
            </div>
            <div className="reader-page-indicator">
              Page <span className="current-page">{pageNumber}</span> of {numPages || '--'}
            </div>
          </div>

          <Document
            file="/menu.pdf?v=2026-2"
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error('PDF load error:', error)}
            loading={
              <div className="pdf-loading-state">
                <div className="luxury-loader__ring" />
                <p>Opening Imperial Menu…</p>
              </div>
            }
            error={
              <div className="pdf-error-state">
                <span className="error-crest">⚜</span>
                <p>Unable to display the PDF menu directly.</p>
                <a 
                  href="/menu.pdf" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="luxury-btn-primary mt-3"
                >
                  Download Menu PDF
                </a>
              </div>
            }
          >
            {/* Navigation Buttons + Page Preview */}
            <div className="pdf-page-viewport">
              <Button
                onClick={previousPage}
                disabled={pageNumber <= 1}
                className="pdf-nav-btn pdf-nav-btn--prev"
                aria-label="Previous Page"
              >
                ‹
              </Button>

              <div className="pdf-page-frame">
                <Page
                  pageNumber={pageNumber}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>

              <Button
                onClick={nextPage}
                disabled={pageNumber >= numPages}
                className="pdf-nav-btn pdf-nav-btn--next"
                aria-label="Next Page"
              >
                ›
              </Button>
            </div>
          </Document>

          {/* Bottom Pagination Toolbar */}
          <div className="pdf-bottom-controls">
            <button
              onClick={previousPage}
              disabled={pageNumber <= 1}
              className="pdf-page-step-btn"
            >
              ← Previous Page
            </button>

            <div className="pdf-counter-badge">
              <span>Page {pageNumber} of {numPages || '--'}</span>
            </div>

            <button
              onClick={nextPage}
              disabled={pageNumber >= numPages}
              className="pdf-page-step-btn"
            >
              Next Page →
            </button>
          </div>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default Menu;